import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { AnsprechpartnerMitHerkunft, KundeListe, TicketBezugsKandidaten, TicketDetail, Unternehmen }
  from "@kundeninfo/api-client";
import { Feld, Icon, useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Auswahl, Chip, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { Dateiwahl, KiKnopf, LoeschKnopf, Suchfeld } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * Reiter des Ticket-Dialogs: Teilaufgaben, Kunden und Bezüge, Anhänge (der Verlauf steht in
 * TicketVerlauf.tsx — die Datei wäre sonst über 400 Zeilen gewachsen). Jeder Schreibweg läuft
 * über `useSchreibweg` (Ruling B7): volles Detail → `onNeu`, 409 → nachladen + Meldung,
 * 422 → Befund am Feld, sonst Hinweis im Reiter.
 */

export type ReiterBasis = {
  d: TicketDetail; client: TicketsClient; onNeu: (d: TicketDetail) => void;
  /** Holt das Detail neu (409 eines Unterobjekts). */
  nachladen: () => Promise<TicketDetail>;
};

export { VerlaufReiter } from "./TicketVerlauf.js";

// ------------------------------------------------------------------ Teilaufgaben (F32)

export function TeilaufgabenReiter(p: ReiterBasis & { entwurf: string; onEntwurf: (w: string) => void }): ReactElement {
  const neu = p.entwurf;
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen, felder: ["title"] });
  const liste = p.d.teile.subtasks;
  const tauschen = (i: number, j: number) => {
    const ids = liste.map((s) => s.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    void weg.schreiben(() => p.client.orderTicketSubtasks(p.d.id, ids), "Die Reihenfolge ließ sich nicht ändern.");
  };
  async function hinzufuegen(): Promise<void> {
    const titel = neu;
    if (await weg.schreiben(() => p.client.addTicketSubtask(p.d.id, titel), "Die Teilaufgabe ließ sich nicht anlegen.")) {
      p.onEntwurf("");
    }
  }
  return (
    <div className="td__teilaufgaben">
      {liste.length === 0 && <p className="td__leer">Noch keine Teilaufgaben.</p>}
      <ol className="td__checkliste">
        {liste.map((s, i) => (
          <li key={s.id} className="td__teilaufgabe">
            {/* Die Gruppe heißt nur für die Vorlesestimme „Teilaufgabe n“; sichtbar steht der Titel
                am Kästchen (Sichtprobe 14, Muster „Einzelkästchen“ in docs/ME-DESIGN.md). */}
            <Optionen mehrfach aria-label={`Teilaufgabe ${i + 1}`} wert={s.doneAt === null ? [] : ["erledigt"]}
              gesperrt={weg.laeuft}
              onGeaendert={(e) => { void weg.schreiben(() => p.client.updateTicketSubtask(p.d.id, s.id, s.version,
                { done: (e.werte ?? []).includes("erledigt") }), "Die Teilaufgabe ließ sich nicht ändern."); }}>
              <option value="erledigt">{s.title}</option>
            </Optionen>
            {s.doneBy !== null && <span className="td__neben">{`erledigt von ${s.doneBy.name}`}</span>}
            <span className="td__aktionen td__aktionen--eng">
              <KiKnopf art="leise" className="td__symbolknopf" name={`${s.title} nach oben`}
                deaktiviert={i === 0 || weg.laeuft} onClick={() => tauschen(i, i - 1)}>
                <Icon name="chevron-up" size={14} />
              </KiKnopf>
              <KiKnopf art="leise" className="td__symbolknopf" name={`${s.title} nach unten`}
                deaktiviert={i === liste.length - 1 || weg.laeuft} onClick={() => tauschen(i, i + 1)}>
                <Icon name="chevron-down" size={14} />
              </KiKnopf>
              <LoeschKnopf className="td__symbolknopf" name={`${s.title} löschen`} onClick={() => {
                void weg.schreiben(() => p.client.deleteTicketSubtask(p.d.id, s.id), "Die Teilaufgabe ließ sich nicht löschen.");
              }} />
            </span>
          </li>
        ))}
      </ol>
      <div className="td__neuzeile">
        <Feld id="td-teilaufgabe" label="Neue Teilaufgabe" wert={neu} bearbeitbar max={300}
          onChange={(w) => { weg.feldRaeumen("title"); p.onEntwurf(w); }}
          fehler={weg.feldFehler.title} />
        <KiKnopf deaktiviert={neu.trim() === "" || weg.laeuft} onClick={() => { void hinzufuegen(); }}>Hinzufügen</KiKnopf>
      </div>
      <SchreibHinweis meldung={weg.meldung} />
    </div>
  );
}

// ------------------------------------------------------------------ Kunden und Bezüge (F42)

type Art = TicketBezugsKandidaten["items"][number]["kind"];
const ARTEN: { art: Art; titel: string }[] = [
  { art: "OFFER", titel: "Angebote" }, { art: "SALES_ORDER", titel: "Aufträge" },
  { art: "INVOICE", titel: "Rechnungen" }, { art: "FILE", titel: "Dateien" },
];

export function KundenReiter(p: ReiterBasis & { companies: readonly Unternehmen[]; onZurAkte: (id: string) => void }): ReactElement {
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const [bezugSuche, setBezugSuche] = useState("");
  const [kandidaten, setKandidaten] = useState<TicketBezugsKandidaten["items"]>([]);
  // Zu welchem Suchtext die Kandidaten geladen sind (null: noch keine Antwort) — der Leertext
  // gilt erst nach einer Antwort, nicht schon beim leeren Suchfeld (Sichtprobe 12).
  const [geladenFuer, setGeladenFuer] = useState<string | null>(null);
  const [kontakte, setKontakte] = useState<AnsprechpartnerMitHerkunft[]>([]);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen });
  const { bestaetigen } = useBestaetigung();
  const sucheNr = useRef(0);
  const kundenSchluessel = p.d.customers.map((c) => c.id).join(",");
  const { client } = p;
  const ticketId = p.d.id;

  // Ansprechpartner der zugeordneten Kunden (nur aktive) für die Auswahl — ohne die geerbten der
  // Konzernkette: Deren Kunde steht nicht am Ticket, der Server wiese sie ab (Gesamtreview I2).
  useEffect(() => {
    let aktiv = true;
    const ids = kundenSchluessel === "" ? [] : kundenSchluessel.split(",");
    Promise.all(ids.map((id) => client.listContacts(id)))
      .then((alle) => { if (aktiv) setKontakte(alle.flatMap((r) => r.items).filter((k) => k.status === "ACTIVE" && !k.inherited)); })
      .catch(() => { if (aktiv) setKontakte([]); });
    return () => { aktiv = false; };
  }, [client, kundenSchluessel]);

  // Bezugs-Kandidaten: je Art die neuesten zuerst (Server, höchstens 20 je Art) — nur mit Kunde.
  useEffect(() => {
    if (kundenSchluessel === "") { setKandidaten([]); setGeladenFuer(null); return undefined; }
    let aktiv = true;
    const text = bezugSuche.trim();
    client.searchTicketReferenceCandidates(ticketId, text)
      .then((r) => { if (aktiv) { setKandidaten(r.items); setGeladenFuer(text); } })
      .catch(() => { if (aktiv) { setKandidaten([]); setGeladenFuer(null); } });
    return () => { aktiv = false; };
  }, [client, ticketId, kundenSchluessel, bezugSuche]);

  async function kundenSuchen(text: string): Promise<void> {
    setSuche(text);
    const nr = ++sucheNr.current;
    if (text.trim().length < 2) { setTreffer([]); return; }
    try {
      const r = await client.getCustomers({ search: text.trim(), limit: 20 });
      if (nr !== sucheNr.current) return;
      setTreffer(r.items.filter((k) => k.relations.some((rel) => rel.shortCode === p.d.companyShortCode)
        && !p.d.customers.some((c) => c.id === k.id)));
    } catch {
      if (nr === sucheNr.current) setTreffer([]);
    }
  }

  async function zuordnen(kundeId: string): Promise<void> {
    if (await weg.schreiben(() => client.addTicketCustomer(ticketId, kundeId), "Der Kunde ließ sich nicht zuordnen.")) {
      setSuche(""); setTreffer([]);
    }
  }

  async function loesen(kundeId: string, name: string): Promise<void> {
    // Kein Lösen ohne Vorschau (F42): Was mit dem Kunden wegfällt, muss vorher dastehen.
    let mit: { contacts: { id: string; name: string }[]; references: { id: string; label: string }[] };
    try {
      mit = await client.previewRemoveTicketCustomer(ticketId, kundeId);
    } catch {
      weg.melden({ art: "fehler", text: `Was mit ${name} wegfiele, ließ sich nicht ermitteln — der Kunde bleibt zugeordnet.` });
      return;
    }
    weg.melden(null);
    // Die Bestätigung ist ein Satz reiner Text (me-design `bestaetigen()`): was wegfällt, steht als
    // Aufzählung im Satz statt als Liste.
    const wegfall = [...mit.contacts.map((c) => `Ansprechpartner ${c.name}`), ...mit.references.map((r) => `Bezug ${r.label}`)];
    const text = wegfall.length === 0
      ? `${name} wird vom Ticket gelöst.`
      : `${name} wird vom Ticket gelöst. Mit dem Kunden fallen weg: ${wegfall.join(", ")}.`;
    if (!(await bestaetigen({ titel: "Kunde lösen?", text, bestaetigenText: "Lösen" }))) return;
    await weg.schreiben(() => client.removeTicketCustomer(ticketId, kundeId), "Der Kunde ließ sich nicht lösen.");
  }

  const freieKontakte = kontakte.filter((k) => !p.d.teile.contacts.some((c) => c.id === k.id));
  const freieKandidaten = kandidaten.filter((k) => !p.d.teile.references.some((r) => r.kind === k.kind && r.targetId === k.targetId));

  return (
    <div className="td__kunden">
      <h4 className="td__ueberschrift">Kunden</h4>
      {p.d.customers.length === 0 && <p className="td__leer">Ohne Kunde (internes Ticket).</p>}
      <ul className="td__liste">
        {p.d.customers.map((c) => (
          <li key={c.id}>
            <Chip {...hausfarbeChip(p.companies, p.d.companyShortCode)}>{`${p.d.companyShortCode} · ${c.name}`}</Chip>
            <KiKnopf art="leise" name={`Akte von ${c.name} öffnen`} onClick={() => p.onZurAkte(c.id)}>Zur Akte</KiKnopf>
            <LoeschKnopf name={`${c.name} lösen`} deaktiviert={weg.laeuft} onClick={() => { void loesen(c.id, c.name); }} />
          </li>
        ))}
      </ul>
      <Suchfeld name="Kunde suchen" platzhalter="Kunde zuordnen (mindestens 2 Zeichen)" wert={suche}
        onText={(t) => { void kundenSuchen(t); }} />
      {treffer.length > 0 && (
        <ul className="td__liste">
          {treffer.map((k) => (
            <li key={k.id}><KiKnopf art="leise" deaktiviert={weg.laeuft} onClick={() => { void zuordnen(k.id); }}>
              {`${k.companyName} zuordnen`}</KiKnopf></li>
          ))}
        </ul>
      )}

      <h4 className="td__ueberschrift">Ansprechpartner</h4>
      {p.d.teile.contacts.length === 0 && <p className="td__leer">Keine Ansprechpartner zugeordnet.</p>}
      <ul className="td__liste">
        {p.d.teile.contacts.map((c) => (
          <li key={c.id}>{c.name}{c.status === "LEFT" ? " (ausgeschieden)" : ""}
            <LoeschKnopf name={`${c.name} lösen`} deaktiviert={weg.laeuft} onClick={() => {
              void weg.schreiben(() => client.removeTicketContact(ticketId, c.id), "Der Ansprechpartner ließ sich nicht lösen.");
            }} /></li>
        ))}
      </ul>
      {p.d.customers.length > 0 && (
        <Auswahl beschriftung="Ansprechpartner zuordnen" wert="" gesperrt={weg.laeuft} onGeaendert={(e) => {
          if (e.wert !== "") {
            void weg.schreiben(() => client.addTicketContact(ticketId, e.wert), "Der Ansprechpartner ließ sich nicht zuordnen.");
          }
        }}>
          <option value="">{freieKontakte.length === 0 ? "– keine weiteren –" : "– wählen –"}</option>
          {freieKontakte.map((k) => <option key={k.id} value={k.id}>{k.anzeigename}</option>)}
        </Auswahl>
      )}

      <h4 className="td__ueberschrift">Bezüge</h4>
      {p.d.teile.references.length === 0 && <p className="td__leer">Keine Bezüge.</p>}
      <ul className="td__liste">
        {p.d.teile.references.map((r) => (
          <li key={r.id}>{r.label}{r.hinweis !== null ? ` (${r.hinweis})` : ""}
            <span className="td__neben">{r.customerName}</span>
            <LoeschKnopf name={`${r.label} lösen`} deaktiviert={weg.laeuft} onClick={() => {
              void weg.schreiben(() => client.removeTicketReference(ticketId, r.id), "Der Bezug ließ sich nicht lösen.");
            }} /></li>
        ))}
      </ul>
      {p.d.customers.length === 0 ? (
        <Hinweis art="info">Kunde zuerst zuordnen — dann lassen sich seine Angebote, Aufträge, Rechnungen und Dateien als Bezug wählen.</Hinweis>
      ) : (
        <>
          <Suchfeld name="Bezug suchen" platzhalter="Angebot, Auftrag, Rechnung oder Datei" wert={bezugSuche}
            onText={setBezugSuche} hilfe="Suchen oder aus den letzten Vorgängen wählen" />
          {geladenFuer !== null && freieKandidaten.length === 0 && (geladenFuer !== "" || kandidaten.length === 0) && (
            <p className="td__leer">
              {geladenFuer !== "" ? "Keine passenden Vorgänge oder Dateien." : "Zu diesem Kunden gibt es keine Vorgänge oder Dateien."}
            </p>
          )}
          {ARTEN.map(({ art, titel }) => {
            const gruppe = freieKandidaten.filter((k) => k.kind === art);
            if (gruppe.length === 0) return null;
            return (
              <section key={art} className="td__kandidaten" aria-label={titel}>
                <h5 className="td__gruppe">{titel}</h5>
                <ul className="td__liste">
                  {gruppe.map((k) => (
                    <li key={`${k.kind}-${k.targetId}`}>
                      <KiKnopf art="leise" name={`${k.label} verknüpfen`} deaktiviert={weg.laeuft} onClick={() => {
                        void weg.schreiben(() => client.addTicketReference(ticketId, k.kind, k.targetId),
                          "Der Bezug ließ sich nicht verknüpfen.");
                      }}>{k.label}</KiKnopf>
                      <span className="td__neben">{k.customerName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
      <SchreibHinweis meldung={weg.meldung} />
    </div>
  );
}

// ------------------------------------------------------------------ Anhänge

function groesse(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** F121: Dateiname gekürzt mit Auslassung, der volle Name im Sofort-Popup (Hausregel statt `title`). */
function AnhangName(p: { name: string; href: string }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(p.name);
  return (
    <span ref={popup} className="td__anhangname">
      <KiKnopf art="leise" href={p.href} download={p.name}>{p.name}</KiKnopf>
    </span>
  );
}

export function AnhaengeReiter(p: ReiterBasis): ReactElement {
  const [laedt, setLaedt] = useState<string[]>([]);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen });
  const { bestaetigen } = useBestaetigung();

  /** Nacheinander hochladen; Fehler je Datei sammeln — ein späterer Erfolg löscht keinen. */
  async function hochladen(dateien: File[]): Promise<void> {
    setLaedt(dateien.map((f) => f.name));
    const fehler: string[] = [];
    try {
      for (const f of dateien) {
        const e = await weg.versuchen(() => p.client.uploadTicketAttachment(p.d.id, f), "Das Hochladen ist fehlgeschlagen.");
        if (e.meldung !== null) fehler.push(`„${f.name}“: ${e.meldung.text}`);
      }
    } finally {
      setLaedt([]);
      weg.melden(fehler.length === 0 ? null : { art: "fehler", text: fehler.join("\n") });
    }
  }

  return (
    <div className="td__anhaenge">
      <Dateiwahl name="Datei hochladen" knopftext="Hochladen" mehrere nachWahlLeeren gesperrt={weg.laeuft}
        hilfe="Höchstens 50 MB je Datei." onDateien={(dateien) => { void hochladen(dateien); }} />
      {laedt.length > 0 && <Hinweis art="info">{`Lädt hoch: ${laedt.join(", ")} …`}</Hinweis>}
      {p.d.teile.attachments.length === 0 && <p className="td__leer">Noch keine Anhänge.</p>}
      <ul className="td__liste">
        {p.d.teile.attachments.map((a) => (
          <li key={a.id} className="td__anhang">
            <AnhangName name={a.originalName} href={p.client.ticketAttachmentUrl(p.d.id, a.id)} />
            <span className="td__neben">{`${groesse(a.sizeBytes)} · ${a.uploadedBy?.name ?? "–"}`}</span>
            <LoeschKnopf name={`${a.originalName} löschen`} deaktiviert={weg.laeuft} onClick={() => { void (async () => {
              if (!(await bestaetigen({ titel: "Anhang löschen?", text: `„${a.originalName}“ wird endgültig gelöscht.`,
                                        bestaetigenText: "Löschen", gefaehrlich: true }))) return;
              await weg.schreiben(() => p.client.deleteTicketAttachment(p.d.id, a.id), "Der Anhang ließ sich nicht löschen.");
            })(); }} />
          </li>
        ))}
      </ul>
      <SchreibHinweis meldung={weg.meldung} />
    </div>
  );
}
